<template>
  <el-form
    ref="ruleFormRef"
    :model="form"
    :rules="rules"
  >
    <el-row
      :gutter="10"
      style="justify-content: space-between"
    >
      <el-col :span="6">
        <el-form-item>
          <custom-data
            :data="form.Codigo"
            :spanColumn="12"
            label="Código"
          />
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item>
          <custom-input
            v-model="form.Prefijo"
            :spanColumn="24"
            label="Prefijo"
            :settings="{
              placeholder: 'Prefijo',
              disabled: disabled,
            }"
          />
        </el-form-item>
      </el-col>
    </el-row>
    <el-form-item prop="Descripcion">
      <custom-input
        ref="descripcionInputRef"
        v-model="form.Descripcion"
        :spanColumn="24"
        label="Descripción"
        :settings="{
          placeholder: 'Descripción',
          disabled: disabled,
        }"
      />
    </el-form-item>
    <el-row
      :gutter="10"
      style="justify-content: space-between"
    >
      <el-col :span="6">
        <el-form-item>
          <custom-switch
            v-model="form.Autonumerico"
            :spanColumn="24"
            label="Autonumerico"
            :settings="{
              disabled: disabled,
            }"
          />
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item>
          <custom-input-number
            v-model="form.ProximoNumero"
            :spanColumn="24"
            label="Próximo número"
            :settings="{
              placeholder: 'Próximo número',
              disabled: disabled,
              min: 0,
              max: 9999999999,
              precision: 0,
              step: 1,
              controls: false,
            }"
          />
        </el-form-item>
      </el-col>
    </el-row>
    <h3>Aplicable a</h3>
    <el-row :gutter="10">
      <el-col :span="4">
        <el-form-item>
          <custom-switch
            v-model="form.Ingreso"
            :spanColumn="24"
            label="Ingreso"
            :settings="{
              disabled: disabled,
            }"
          />
        </el-form-item>
      </el-col>
      <el-col :span="4">
        <el-form-item>
          <custom-switch
            v-model="form.Egreso"
            :spanColumn="24"
            label="Egreso"
            :settings="{
              disabled: disabled,
            }"
          />
        </el-form-item>
      </el-col>
      <el-col :span="5">
        <el-form-item>
          <custom-switch
            v-model="form.Transferencia"
            :spanColumn="24"
            label="Transferencia"
            :settings="{
              disabled: disabled,
            }"
          />
        </el-form-item>
      </el-col>
    </el-row>
    <el-form-item>
      <additional-settings-buttons
        :disabled="disabled"
        @add="handleClickAdd"
        @save="handleClickSave"
        @cancel="handleClickCancel"
      />
    </el-form-item>
  </el-form>
  <additional-settings-table
    :data="talonarios"
    :columns="columns"
    :height="200"
    :requestApi="talonarioStore.findAllTalonarioHTTP"
    @edit="handleClickEdit"
    @delete="handleClickDelete"
  />
</template>

<script setup>
import {
  onMounted,
  ref,
  reactive,
  nextTick,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import AdditionalSettingsButtons from './additional_settings_buttons.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomData from '@/shared/components/custom_data.vue';
import CustomSwitch from '@/shared/components/custom_switch.vue';
import { useTalonarioStore } from '@/stores/talonario/talonario_store';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import AdditionalSettingsTable from './additional_settings_table.vue';
import talonario_model from '@/shared/models/talonario_model.js';

const talonarioStore = useTalonarioStore();
const { talonarios, talonario } = storeToRefs(talonarioStore);

const columns = [
  { field: 'Codigo', label: 'ID', width: 75, sortable: true },
  { field: 'Descripcion', label: 'Descripción', width: 280, sortable: true },
];

const ruleFormRef = ref(null);
const descripcionInputRef = ref(null);

const form = reactive({ ...talonario_model });

const rules = reactive({
  Descripcion: [{ required: true, message: 'Ingrese la descripción', trigger: 'blur' }],
});

const disabled = ref(true);

const handleClickAdd = () => {
  select();
  talonarioStore.cleanTalonario();
};

const handleClickEdit = (row) => {
  if (row.Codigo) {
    select(row);
  }
};

const select = (item) => {
  if (item) {
    Object.assign(form, item);
  } else {
    Object.assign(form, talonario_model);
  }
  disabled.value = false;
  nextTick(() => {
    descripcionInputRef.value.focus();
  });
};

const handleClickDelete = (row) => {
  if (row.Codigo) {
    ElMessageBox.confirm(`¿Quiere eliminar el talonario ${row.Descripcion}?`, 'Confirmación', {
      confirmButtonText: 'Si',
      cancelButtonText: 'No',
      type: 'warning',
    })
      .then(() => {
        deleteTalonario(row);
      })
      .catch((err) => {
        return;
      });
  }
};

const deleteTalonario = async (row) => {
  try {
    useLoading().openLoading();

    await talonarioStore.deleteTalonarioHTTP(row);
    clean();
    await talonarioStore.firstRequest();
    useMessageSuccess().open(`El talonario se eliminó exitosamente!`);
  } catch (error) {
    useMessageError().open(`Ocurrió un error al eliminar el talonario!`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleClickSave = async () => {
  try {
    if (!ruleFormRef.value) return;

    const valid = await ruleFormRef.value.validate();

    if (!valid) {
      return;
    }
    await save();
  } catch (err) {}
};

const save = async () => {
  try {
    talonario.value = { ...form };

    useLoading().openLoading();

    await talonarioStore.saveTalonario();
    clean();
    await talonarioStore.firstRequest();
    useMessageSuccess().open(`Los datos se guardaron exitosamente!`);
  } catch (error) {
    useMessageError().open(`Ocurrió un error al guardar los datos!`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleClickCancel = () => {
  clean();
};

const clean = () => {
  disabled.value = true;
  talonarioStore.cleanTalonario();
  ruleFormRef.value.clearValidate();
  Object.assign(form, talonario_model);
};

onMounted(async () => {
  await talonarioStore.firstRequest();
});
</script>
